/**
 * Saved views: named filters, applied with one click.
 *
 * A view stores the QUESTION, not the answer — «مشتاق اشتراک بدون تعامل ۳۰
 * روز» has to mean "as of today", which a frozen member list could not. Opening
 * one therefore re-runs the search rather than restoring rows.
 */
import { useMutation } from '@apollo/client';
import { Button } from '@meshhub/design-tokens/ui';
import { Bookmark, Save, Trash2, Users } from 'lucide-react';
import React, { useState } from 'react';
import { filterFromStoredJson, isEmptyFilter, toFilterInput } from '../../lib/filters.js';
import { CREATE_SAVED_VIEW, DELETE_SAVED_VIEW } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

export function SavedViewBar({ views, filter, isAdmin, onApply, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [share, setShare] = useState(false);
  const [error, setError] = useState(null);

  const [createView, { loading }] = useMutation(CREATE_SAVED_VIEW);
  const [deleteView] = useMutation(DELETE_SAVED_VIEW);

  const save = async () => {
    setError(null);
    try {
      await createView({
        variables: { name: name.trim(), filter: toFilterInput(filter), isShared: share },
      });
      setName('');
      setShare(false);
      setSaving(false);
      onChanged();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-border bg-surface px-6 py-2"
      aria-label={S.search.savedViews}
    >
      <Bookmark size={14} aria-hidden="true" className="text-text-muted" />
      {views.length === 0 && (
        <span className="text-sm text-text-muted">{S.search.savedViews}</span>
      )}
      {views.map((view) => (
        <span key={view.id} className="flex items-center">
          <button
            type="button"
            onClick={() => onApply(filterFromStoredJson(view.filterJson))}
            className="flex h-control items-center gap-1 rounded-s border border-border
                       bg-surface px-3 text-sm hover:bg-surface-hover"
          >
            {view.isShared && <Users size={11} aria-hidden="true" className="text-text-muted" />}
            {view.name}
          </button>
          {/* Seeded views are the deployment's, not anybody's to delete. */}
          {!view.isSystem && (
            <button
              type="button"
              title={S.search.deleteView}
              aria-label={`${S.search.deleteView}: ${view.name}`}
              onClick={async () => {
                await deleteView({ variables: { id: view.id } });
                onChanged();
              }}
              className="flex h-control items-center rounded-e border border-s-0 border-border
                         bg-surface px-2 text-text-muted hover:text-danger"
            >
              <Trash2 size={12} aria-hidden="true" />
            </button>
          )}
        </span>
      ))}

      <div className="ms-auto flex items-center gap-2">
        {saving ? (
          <>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={S.search.saveViewName}
              autoFocus
              className="h-control w-56 rounded border border-border bg-bg px-3 text-sm
                         outline-none focus:border-primary"
            />
            {isAdmin && (
              <label className="flex items-center gap-1 text-xs text-text-muted">
                <input
                  type="checkbox"
                  className="accent-primary"
                  checked={share}
                  onChange={(event) => setShare(event.target.checked)}
                />
                {S.search.shareView}
              </label>
            )}
            <Button variant="primary" disabled={!name.trim() || loading} onClick={save}>
              {S.app.save}
            </Button>
            <Button variant="ghost" onClick={() => setSaving(false)}>
              {S.app.cancel}
            </Button>
          </>
        ) : (
          <Button
            variant="ghost"
            // Saving "no filters" would create a view that means «همه», which
            // the contacts screen already is.
            disabled={isEmptyFilter(filter)}
            onClick={() => setSaving(true)}
          >
            <Save size={13} aria-hidden="true" />
            {S.search.saveView}
          </Button>
        )}
      </div>
      {error && <p className="w-full text-sm text-danger">{error}</p>}
    </div>
  );
}
